import {createSlice, createAsyncThunk} from '@reduxjs/toolkit';
import {validateFieldMultiple} from '../../../utilities/validation/validator';
import ProviderManager from '../../../provider/providerManager';
import lodash from 'lodash';
import {regexPatterns} from '../../../utilities/validation/validationVariables';
import { showAppLoader, hideAppLoader } from '../../Shared/Loader/LoaderSlice';

export const doSearch = createAsyncThunk('HeaderSearch/doSearch', async (data, thunkAPI) => {
  try {
    const result = await ProviderManager.getProviderData('fundialSearch', [data]);
    return result.Body.itemsFolders;
  } catch (error) {
    return error;
  }
});

export const getCategory = createAsyncThunk('fundialPage/getCategory', async (data, thunkAPI) => {
  try {
    const result = await ProviderManager.getProviderData('fundialGetCategory', [data]);
    return {results: {id: data, folders: result.Body.itemsFolders.folder, items: result.Body.itemsFolders.item}, errorCode: 0};
  } catch (error) {
    return {errorCode: error.errorCode};
  }
});

export const getSubscribers = createAsyncThunk('fundialPage/getSubscribers', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());
    const result = await ProviderManager.getProviderData('fundialGetSubscribers', []);
    thunkAPI.dispatch(hideAppLoader());
    return {results: {subscribers: result.Body.subscribersData}, errorCode: 0};
  } catch (error) {
    thunkAPI.dispatch(hideAppLoader());
    const authenticationState = thunkAPI.getState().authentication;
    const userIsAuthenticated = authenticationState?.loginData?.isFullAuthenticated || authenticationState?.loginData?.isOtpAuthenticated || authenticationState?.loginData?.isOtpExtendedAuthenticated || authenticationState?.loginData?.isSilentOtpAuthenticated;
    if (userIsAuthenticated) {
      thunkAPI.dispatch(resetPage());
    }

    switch (error.errorCode) {
      case 89:
        thunkAPI.dispatch(setErrorType({errorType: 'SUBSCRIBER_ERROR'}));
        thunkAPI.dispatch(goToPage({page: 'errorPage'}));
        break;
      default:
        thunkAPI.dispatch(setErrorType({errorType: 'GENERAL'}));
        thunkAPI.dispatch(goToPage({page: 'errorPage'}));
        break;
    }

    return {errorCode: error.errorCode};
  }
});

export const getGallery = createAsyncThunk('fundialPage/getGallery', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(setIsFinishedGetGallery(false));
    const result = await ProviderManager.getProviderData('fundialGetGallery', [data.subscriber]);
    // thunkAPI.dispatch(pageLoaded());
    return {results: {subscriberGallery: result.Body.subscriberGallery}, errorCode: 0};
  } catch (error) {
    switch (error.errorCode) {
      case 92:
        thunkAPI.dispatch(initFundial());
        break;
      default:
        thunkAPI.dispatch(setErrorType({errorType: 'GENERAL'}));
        thunkAPI.dispatch(goToPage({page: 'errorPage'}));
    }
    return {errorCode: error.errorCode};
  }
});

export const getAoc = createAsyncThunk('fundialPage/getAoc', async (data, thunkAPI) => {
  try {
    const result = await ProviderManager.getProviderData('fundialGetAoc', [data.subscriber, data.productId]);
    return {results: {aocDetails: result.Body}, errorCode: 0};
  } catch (error) {
    switch (error.errorCode) {
      case 21:
      case 22:
      case 23:
      case 24:
      case 89:
        thunkAPI.dispatch(setErrorType({errorType: 'SUBSCRIBER_ERROR'}));
        thunkAPI.dispatch(goToPage({page: 'errorPage'}));
        break;
      case 20:
        thunkAPI.dispatch(setErrorType({errorType: 'SONG_EXIST'}));
        thunkAPI.dispatch(goToPage({page: 'errorPage'}));
        break;
      default:
        thunkAPI.dispatch(setErrorType({errorType: 'GENERAL'}));
        thunkAPI.dispatch(goToPage({page: 'errorPage'}));
    }
    return {errorCode: error.errorCode, returnCodeMessage: error.returnCodeMessage};
  }
});

export const setDefaultSong = createAsyncThunk('fundialPage/setDefaultSong', async (data, thunkAPI) => {
  try {
    const result = await ProviderManager.getProviderData('fundialSetDefaultSong', [data.subscriber, data.productId]);
    thunkAPI.dispatch(getGallery({subscriber: data.subscriber}));
    return {results: {isSuccess: true}, errorCode: 0};
  } catch (error) {
    return {errorCode: error.errorCode};
  }
});

export const setShuffle = createAsyncThunk('fundialPage/setShuffle', async (data, thunkAPI) => {
  try {
    const result = await ProviderManager.getProviderData('fundialSetShuffle', [data.subscriber, data.profileId, data.isSetShuffle]);
    thunkAPI.dispatch(getGallery({subscriber: data.subscriber}));
    return {results: {isSuccess: true, isSetShuffle: data.isSetShuffle}, errorCode: 0};
  } catch (error) {
    return {errorCode: error.errorCode};
  }
});

export const deleteSong = createAsyncThunk('fundialPage/deleteSong', async (data, thunkAPI) => {
  try {
    const result = await ProviderManager.getProviderData('fundialDeleteSong', [data.subscriber, data.productId]);
    thunkAPI.dispatch(getGallery({subscriber: data.subscriber}));
  } catch (error) {
    return {errorCode: error.errorCode};
  }
});

export const deleteContact = createAsyncThunk('fundialPage/deleteContact', async (data, thunkAPI) => {
  try {
    const result = await ProviderManager.getProviderData('fundialDeleteContact', [data.subscriber, data.profileId]);
    thunkAPI.dispatch(getGallery({subscriber: data.subscriber}));
  } catch (error) {
    return {errorCode: error.errorCode};
  }
});

export const addContact = createAsyncThunk('fundialPage/addContact', async (data, thunkAPI) => {
  try {
    const result = await ProviderManager.getProviderData('fundialAddContact', [data.subscriber, data.songId, data.phoneNumbers]);
    thunkAPI.dispatch(getGallery({subscriber: data.subscriber}));
    return {results: {isSuccess: true}, errorCode: 0};
  } catch (error) {
    return {errorCode: error.errorCode, returnCodeMessage: error.returnCodeMessage};
  }
});

export const purchaseSong = createAsyncThunk('fundialPage/purchase', async (data, thunkAPI) => {
  try {
    const state = thunkAPI.getState().fundialPage;
    const isPremium = state.aocDetails?.fundialPurchaseType == 'PremiumItem';

    const result = await ProviderManager.getProviderData('fundialPurchase', [data.subscriber, data.orderString, data.isDefault, isPremium]);
    thunkAPI.dispatch(getGallery({subscriber: data.subscriber}));
    return {results: {isSuccess: true}, errorCode: 0};
  } catch (error) {
    switch (error.errorCode) {
      case 12:
        thunkAPI.dispatch(setErrorType({errorType: 'PLAYLIST_EXCEED_ERROR'}));
        thunkAPI.dispatch(goToPage({page: 'errorPage'}));
        break;
      default:
        thunkAPI.dispatch(setErrorType({errorType: 'GENERAL'}));
        thunkAPI.dispatch(goToPage({page: 'errorPage'}));
    }
    return {errorCode: error.errorCode, returnCodeMessage: error.returnCodeMessage};
  }
});

const initialState = {
  form: {
    phones: {},
    valid: '',
    multipleErrors: {},
    errors: {},
    rules: {
      phone: {
        required: {value: true, error: 'אנא הכנס מספר טלפון'},
        pattern: {value: regexPatterns.phoneGeneralRegex, error: 'ערך לא תקין'},
        minLength: {value: 9, error: 'חובה להזין מספר טלפון תקין'},
        maxLength: {value: 10, error: 'חובה להזין מספר טלפון תקין'},
      },
    },
  },
  navigation: {
    isBackFromLogin: false,
    pageBackFromOtp: null,
    currentPage: '',
    titleBold: 'המתנה נעימה',
    titleRegular: '',
  },
  search: {
    searchResults: [],
    openResultSearch: false,
  },
  pages: {
    catalogPage: {
      selectedCategory: {},
      arabicFirstFolderCategory: null,
    },
    categoryPage: {
      selectedCategory: {},
    },
    songListPage: {
      selectedCategory: {},
    },
    songSetupSuccessPage: {},
    errorPage: {
      errorType: '',
    },
    personalAreaPage: {
      isDefaultSongSelected: false,
      isFinishedGetGallery: false,
      defaultSong: null,
    },
  },
  catagories: {},
  selectedSong: {},
  subscribers: [],
  isFinishedGetSubscribers: false,
  selectedSubscriber: null,
  subscriberGallery: null,
  aocDetails: null,
  showDownloadSongPopUp: false,
  popOverOpenId: null,
  isInitFundialComplete: false,
  stripMassage: {isShow: false, message: ''},
  isBackNavigationDone: false,
  isListenerAdded: false,
};

const fundialSlice = createSlice({
  name: 'fundialPage',
  initialState: initialState,
  reducers: {
    resetPage: (state) => initialState,
    initFundial: {
      reducer: (state, action) => {
        state.navigation.currentPage = '';
        state.navigation.pageBackFromOtp = null;
        state.navigation.titleBold = 'המתנה נעימה';
        state.navigation.titleRegular = '';
        state.subscribers = [];
        state.selectedSubscriber = null;
        state.selectedSong = null;
        state.showDownloadSongPopUp = false;
        state.isFinishedGetSubscribers = false;
        state.isInitSaleComplete = true;
        state.isBackNavigationDone = false;
        state.isListenerAdded = false;
      },
    },
    validateHandler: {
      reducer(state, action) {
        state.form = action.payload;
      },
      prepare(payload) {
        const id = payload.target.id;
        const value = payload.target.value;
        const ruleName = id.split('-')[0];
        const form = lodash.cloneDeep(payload.form);
        const validate = validateFieldMultiple(form, id, value, ruleName);

        if (validate.multipleErrors[id] == '') {
          validate.valid = true;
        }
        return {payload: validate};
      },
    },
    clearIsValid: {
      reducer(state, action) {
        const form = {...state.form}; // your state form obj
        form.valid = '';
        state['form'] = form;
      },
    },
    clearPhone: {
      reducer(state, action) {
        const form = {...state.form}; // your state form obj
        form.phones[action.payload] = {isActive: false, value: ''};
        state['form'] = form;
      },
    },
    blurHandler: {
      reducer(state, action) {
        state.form = action.payload;
      },
      prepare(payload) {
        const id = payload.target.id;
        const value = payload.target.value;
        const form = lodash.cloneDeep(payload.form);
        const ruleName = id.split('-')[0];
        const validate = validateFieldMultiple(form, id, value, ruleName);
        return {payload: validate};
      },
    },
    changeHandler: {
      reducer(state, action) {
        state.form = action.payload;
      },
      prepare(payload) {
        const id = payload.target.id;
        const value = payload.target.value;
        const form = lodash.cloneDeep(payload.form);
        const ruleName = id.split('-')[0];
        const validate = validateFieldMultiple(form, id, value, ruleName);

        if (form.phones[id].value.length > 10) {
          form.phones[id] = {isActive: false, value: form.phones[id].slice(0, 10)};
          form.multipleErrors[id] = '';
        }

        if (form.phones[id].length < 10) form.multipleErrors[id] = '';

        return {payload: validate};
      },
    },
    setPageBackFromOtp: (state, action) => {
      state.navigation.pageBackFromOtp = action.payload.pageBackFromOtp;
    },
    setIsBackFromLogin: (state, action) => {
      state.isBackFromLogin = action.payload;
    },
    goToPage: (state, action) => {
      state.navigation.currentPage = action.payload.page;
    },
    navigateTo: (state, action) => {
      // action for invoke saveHistory function
    },
    setFlowType: (state, action) => {
      state.flowType = action.payload.flowType;
    },

    setTitle: (state, action) => {
      state.navigation.titleBold = action.payload.titleBold;
      state.navigation.titleRegular = action.payload.titleRegular;
    },
    setSelectedSong: (state, action) => {
      state.selectedSong = action.payload;
    },
    setSelectedSubscriber: (state, action) => {
      state.selectedSubscriber = action.payload;
    },
    setPopOverOpenId: (state, action) => {
      state.popOverOpenId = action.payload;
    },
    setShowDownloadSongPopUp: (state, action) => {
      state.showDownloadSongPopUp = action.payload;
    },
    addPhoneInput: (state, action) => {
      state.form.phones[action.payload.id] = {isActive: action.payload.isActive, value: action.payload.value};
    },
    setSelectedCategory: (state, action) => {
      if (action.payload.page == 'categoryPage') {
        state.pages.categoryPage.selectedCategory = {id: action.payload.id, title: action.payload.title};
      }
      if (action.payload.page == 'songListPage') {
        state.pages.songListPage.selectedCategory = {id: action.payload.id, title: action.payload.title};
      }
    },
    clearSearchResults: (state, action) => {
      state.search.searchResults = [];
    },
    setStripMassage: (state, action) => {
      state.stripMassage = {isShow: action.payload.isShow, isErrorMsg: action.payload.isErrorMsg, message: action.payload.message};
    },
    setErrorType: (state, action) => {
      state.pages.errorPage.errorType = action.payload.errorType;
    },
    setSongSetupSuccessPage: (state, action) => {
      state.pages.songSetupSuccessPage = {type: action.payload.type};
    },
    pageLoaded: (state, action) => {},
    setIsDefaultSong: (state, action) => {
      state.pages.personalAreaPage.isDefaultSongSelected = action.payload;
    },
    setDefaultSongSetting: (state, action) => {
      state.pages.personalAreaPage.defaultSong = action.payload;
    },
    setArabicFirstFolderCategory: (state, action) => {
      state.pages.catalogPage.arabicFirstFolderCategory = action.payload;
    },
    setIsFinishedGetGallery: (state, action) => {
      state.pages.personalAreaPage.isFinishedGetGallery = action.payload;
    },
    setIsBackNavigationDone: (state, action) => {
      state.isBackNavigationDone = action.payload;
    },
    setIsListenerAdded: (state, action) => {
      state.isListenerAdded = action.payload;
    },
    reportAnalytics: (state, action) => {},
  },
  extraReducers: {
    [doSearch.fulfilled]: (state, action) => {
      state.search.searchResults = action.payload.item;
    },
    [getCategory.fulfilled]: (state, action) => {
      if (action.payload.errorCode == 0) {
        state.catagories[action.payload.results.id] = {
          isFolders: action.payload.results.folders.length != 0,
          folders: action.payload.results.folders,
          items: action.payload.results.items,
        };
      }
    },
    [getSubscribers.fulfilled]: (state, action) => {
      state.isFinishedGetSubscribers = true;
      if (action.payload.errorCode == 0) {
        state.subscribers = action.payload.results.subscribers;
        if (state.subscribers.length == 1) state.selectedSubscriber = state.subscribers[0];
      }
    },
    [getGallery.fulfilled]: (state, action) => {
      state.pages.personalAreaPage.isFinishedGetGallery = true;
      if (action.payload.errorCode == 0) {
        state.subscriberGallery = action.payload.results.subscriberGallery;
      }
    },
    [getAoc.fulfilled]: (state, action) => {
      if (action.payload.errorCode == 0) {
        state.aocDetails = action.payload.results.aocDetails;
      }
    },
    [setDefaultSong.fulfilled]: (state, action) => {},
    [setShuffle.fulfilled]: (state, action) => {
      if (action.payload.errorCode == 0) {
        state.stripMassage = {isShow: true, isErrorMsg: false, message: 'הפעולה בוצעה בהצלחה'};
        if (action.payload.results.isSetShuffle) {
          state.pages.personalAreaPage.isDefaultSongSelected = false;
        } else {
          state.pages.personalAreaPage.isDefaultSongSelected = true;
        }
      } else {
        state.stripMassage = {isShow: true, isErrorMsg: true, message: 'הפעולה לא הצליחה'};
      }
    },
    [deleteSong.fulfilled]: (state, action) => {
      if (action.payload == undefined) {
        state.stripMassage = {isShow: true, isErrorMsg: false, message: 'הפעולה בוצעה בהצלחה'};
      } else {
        state.stripMassage = {isShow: true, isErrorMsg: true, message: 'הפעולה לא הצליחה'};
      }
    },
    [deleteContact.fulfilled]: (state, action) => {
      if (action.payload == undefined) {
        state.stripMassage = {isShow: true, isErrorMsg: false, message: 'הפעולה בוצעה בהצלחה'};
      } else {
        state.stripMassage = {isShow: true, isErrorMsg: true, message: 'הפעולה לא הצליחה'};
      }
    },
    [addContact.fulfilled]: (state, action) => {},
    [purchaseSong.fulfilled]: (state, action) => {},
  },
});

export const {
  resetPage,
  initFundial,
  setPageBackFromOtp,
  setIsBackFromLogin,
  addSelectedPhone,
  removeSelectedPhone,
  setCellularServices,
  setProductsInfo,
  goToPage,
  setFlowType,
  validateHandler,
  clearIsValid,
  clearPhone,
  blurHandler,
  changeHandler,
  setTitle,
  setSelectedSong,
  setSelectedSubscriber,
  setPopOverOpenId,
  setShowDownloadSongPopUp,
  addPhoneInput,
  setSelectedCategory,
  clearSearchResults,
  setStripMassage,
  setSongSetupSuccessPage,
  setErrorType,
  pageLoaded,
  setIsDefaultSong,
  setDefaultSongSetting,
  setArabicFirstFolderCategory,
  setIsFinishedGetGallery,
  setIsListenerAdded,
  setIsBackNavigationDone,
  reportAnalytics,
} = fundialSlice.actions;

export default fundialSlice.reducer;
